import { stText } from "../../i18n/streamingPagesPresentation";
/**
 * template-fields -- Field definitions for the WSD Template Manager.
 *
 * Defines all configurable fields organized by category. Each field has a
 * type, label, description, default value, and optional server-type filter.
 * Used by TemplateManager.tsx to render the editor UI.
 */

// ============================================================================
// Types
// ============================================================================

export type FieldType = "boolean" | "number" | "string" | "string[]" | "enum";

/** Which top-level bucket the field belongs to in the template payload */
export type FieldBucket = "policy" | "config" | "wsdLocal";

export interface FieldDef {
  /** Key name matching the template payload field (e.g. "IsDisabled") */
  key: string;
  /** Which bucket: policyFields, configFields, or wsdLocalFields */
  bucket: FieldBucket;
  /** UI type for the value control */
  type: FieldType;
  /** Display label */
  label: string;
  /** Short description */
  description: string;
  /** Default value when first included */
  defaultValue: boolean | number | string | string[];
  /** Restrict to a specific server type (null = both) */
  serverOnly?: "emby" | "jellyfin" | null;
  /** Enum options (only when type === "enum") */
  enumOptions?: Array<{ value: string; label: string }>;
  /** Min value (only when type === "number") */
  min?: number;
  /** Max value (only when type === "number") */
  max?: number;
  /** Unit label for numbers (e.g. "bps") */
  unit?: string;
  /** Intent ID for the PATCH settings API (maps to userIntentMap.ts) */
  intentId?: string;
}

export interface FieldCategory {
  id: string;
  title: string;
  icon: string;
  accent: "amber" | "sky" | "violet" | "rose" | "slate" | "emerald" | "cyan" | "pink" | "indigo" | "teal" | "orange";
  fields: FieldDef[];
}

// ============================================================================
// Subtitle mode enum options
// ============================================================================

const subtitleModeOptions: Array<{ value: string; label: string }> = [
  { value: "Default", get label() { return stText("default"); } },
  { value: "Always", get label() { return stText("alwaysShow"); } },
  { value: "OnlyForced", get label() { return stText("onlyForced"); } },
  { value: "None", get label() { return stText("none"); } },
  { value: "Smart", get label() { return stText("smart"); } },
];

const introSkipOptions: Array<{ value: string; label: string }> = [
  { value: "None", get label() { return stText("none"); } },
  { value: "ShowButton", get label() { return stText("showButton"); } },
  { value: "AutoSkip", get label() { return stText("autoSkip"); } },
];

// ============================================================================
// Category definitions
// ============================================================================

export const TEMPLATE_CATEGORIES: FieldCategory[] = [
  {
    id: "account",
    get title() { return stText("accountStatus"); },
    icon: "lucide:shield",
    accent: "amber",
    fields: [
      { key: "IsDisabled", bucket: "policy", type: "boolean", get label() { return stText("accountDisabled"); }, get description() { return stText("preventsTheUserFromSigningIn"); }, defaultValue: false, intentId: "disabled" },
      { key: "IsHidden", bucket: "policy", type: "boolean", get label() { return stText("hiddenUser"); }, get description() { return stText("hidesTheUserFromLoginScreensAndUserLists"); }, defaultValue: false, intentId: "hidden" },
      { key: "IsHiddenRemotely", bucket: "policy", type: "boolean", get label() { return stText("hiddenRemotely"); }, get description() { return stText("hidesTheUserFromRemoteLoginScreens"); }, defaultValue: false, serverOnly: "emby", intentId: "hiddenRemotely" },
      { key: "IsHiddenFromUnusedDevices", bucket: "policy", type: "boolean", get label() { return stText("hiddenFromNewDevices"); }, get description() { return stText("hidesTheUserOnDevicesTheyHaveNotUsed"); }, defaultValue: false, serverOnly: "emby", intentId: "hiddenFromNewDevices" },
    ],
  },
  {
    id: "access",
    get title() { return stText("accessStreaming"); },
    icon: "lucide:play-circle",
    accent: "sky",
    fields: [
      { key: "EnableRemoteAccess", bucket: "policy", type: "boolean", get label() { return stText("remoteAccess"); }, get description() { return stText("allowThisUserToConnectRemotely"); }, defaultValue: true, intentId: "remoteAccess" },
      { key: "EnableMediaPlayback", bucket: "policy", type: "boolean", get label() { return stText("mediaPlayback"); }, get description() { return stText("allowMediaPlaybackOnTheServer"); }, defaultValue: true, intentId: "allowPlayback" },
      { key: "SimultaneousStreamLimit", bucket: "policy", type: "number", get label() { return stText("streamLimit"); }, get description() { return stText("maximumSimultaneousStreams0Unlimited"); }, defaultValue: 0, min: 0, max: 20, intentId: "simultaneousStreams" },
      { key: "RemoteClientBitrateLimit", bucket: "policy", type: "number", get label() { return stText("bitrateLimit"); }, get description() { return stText("remoteClientBitrateLimit0Unlimited"); }, defaultValue: 0, min: 0, unit: "bps", intentId: "bitrateLimit" },
    ],
  },
  {
    id: "transcoding",
    get title() { return stText("playbackTranscoding"); },
    icon: "lucide:cpu",
    accent: "violet",
    fields: [
      { key: "EnableAudioPlaybackTranscoding", bucket: "policy", type: "boolean", get label() { return stText("audioTranscoding"); }, get description() { return stText("allowAudioTranscodingDuringPlayback"); }, defaultValue: true, intentId: "audioTranscoding" },
      { key: "EnableVideoPlaybackTranscoding", bucket: "policy", type: "boolean", get label() { return stText("videoTranscoding"); }, get description() { return stText("allowVideoTranscodingDuringPlayback"); }, defaultValue: true, intentId: "videoTranscoding" },
      { key: "EnablePlaybackRemuxing", bucket: "policy", type: "boolean", get label() { return stText("remuxing"); }, get description() { return stText("allowContainerRemuxingDuringPlayback"); }, defaultValue: true, intentId: "remuxing" },
      { key: "EnableSyncTranscoding", bucket: "policy", type: "boolean", get label() { return stText("syncTranscoding"); }, get description() { return stText("allowTranscodingForOfflineSync"); }, defaultValue: true, intentId: "syncTranscoding" },
      { key: "EnableMediaConversion", bucket: "policy", type: "boolean", get label() { return stText("mediaConversion"); }, get description() { return stText("allowMediaFormatConversion"); }, defaultValue: true, intentId: "mediaConversion" },
      { key: "AutoRemoteQuality", bucket: "policy", type: "number", get label() { return stText("autoRemoteQuality"); }, get description() { return stText("automaticQualitySettingForRemotePlayback"); }, defaultValue: 0, min: 0, serverOnly: "emby", intentId: "autoRemoteQuality" },
    ],
  },
  {
    id: "library",
    get title() { return stText("libraryAccess"); },
    icon: "lucide:library",
    accent: "emerald",
    fields: [
      { key: "EnableAllFolders", bucket: "policy", type: "boolean", get label() { return stText("accessAllLibraries"); }, get description() { return stText("grantAccessToAllMediaLibraries"); }, defaultValue: true, intentId: "allFolders" },
      { key: "EnabledFolderNames", bucket: "policy", type: "string[]", get label() { return stText("allowedLibraries"); }, get description() { return stText("specificLibraryNamesToAllowAccess"); }, defaultValue: [], intentId: "enabledFolders" },
      { key: "ExcludedSubFolders", bucket: "policy", type: "string[]", get label() { return stText("excludedSubFolders"); }, get description() { return stText("excludeSpecificMediaLocationsWithinLibrariesEG4K"); }, defaultValue: [], intentId: "excludedSubFolders", serverOnly: "emby" },
      { key: "EnableAllChannels", bucket: "policy", type: "boolean", get label() { return stText("accessAllChannels"); }, get description() { return stText("grantAccessToAllChannels"); }, defaultValue: true, intentId: "allChannels" },
    ],
  },
  {
    id: "content",
    get title() { return stText("contentManagement"); },
    icon: "lucide:folder",
    accent: "teal",
    fields: [
      { key: "EnableContentDeletion", bucket: "policy", type: "boolean", get label() { return stText("allowDeletion"); }, get description() { return stText("allowThisUserToDeleteContentFromTheServer"); }, defaultValue: false, intentId: "allowDeletion" },
      { key: "EnableContentDownloading", bucket: "policy", type: "boolean", get label() { return stText("allowDownloads"); }, get description() { return stText("allowThisUserToDownloadContent"); }, defaultValue: true, intentId: "allowDownload" },
      { key: "EnableSubtitleDownloading", bucket: "policy", type: "boolean", get label() { return stText("subtitleDownloads"); }, get description() { return stText("allowDownloadingExternalSubtitles"); }, defaultValue: true, intentId: "subtitleDownloading" },
      { key: "EnableSubtitleManagement", bucket: "policy", type: "boolean", get label() { return stText("subtitleManagement"); }, get description() { return stText("allowManagingSubtitlesOnTheServer"); }, defaultValue: false, intentId: "subtitleManagement" },
    ],
  },
  {
    id: "parental",
    get title() { return stText("parentalControls"); },
    icon: "lucide:baby",
    accent: "rose",
    fields: [
      { key: "IsTagBlockingModeInclusive", bucket: "policy", type: "boolean", get label() { return stText("tagModeAllowlist"); }, get description() { return stText("useInclusiveTagBlockingMode"); }, defaultValue: false, intentId: "tagBlockingInclusive" },
      { key: "AllowTagOrRating", bucket: "policy", type: "boolean", get label() { return stText("allowIfTagORRatingPasses"); }, get description() { return stText("allowContentIfEitherTagOrRatingCheckPasses"); }, defaultValue: false, serverOnly: "emby", intentId: "allowTagOrRating" },
      { key: "MaxParentalRating", bucket: "policy", type: "number", get label() { return stText("maxRating"); }, get description() { return stText("maximumAllowedParentalRating0None"); }, defaultValue: 0, min: 0, intentId: "maxParentalRating" },
      { key: "BlockedTags", bucket: "policy", type: "string[]", get label() { return stText("blockedTags"); }, get description() { return stText("tagsToBlockFromThisUser"); }, defaultValue: [], intentId: "blockedTags" },
      { key: "IncludeTags", bucket: "policy", type: "string[]", get label() { return stText("includeTags"); }, get description() { return stText("tagsToIncludeForThisUser"); }, defaultValue: [], intentId: "includeTags" },
      { key: "BlockUnratedItems", bucket: "policy", type: "string[]", get label() { return stText("blockUnratedItems"); }, get description() { return stText("contentTypesToBlockWhenUnrated"); }, defaultValue: [], intentId: "blockUnratedItems" },
    ],
  },
  {
    id: "social",
    get title() { return stText("socialSharing"); },
    icon: "lucide:share-2",
    accent: "pink",
    fields: [
      { key: "EnablePublicSharing", bucket: "policy", type: "boolean", get label() { return stText("publicSharing"); }, get description() { return stText("allowPublicMediaSharingLinks"); }, defaultValue: false, intentId: "publicSharing" },
      { key: "AllowSharingPersonalItems", bucket: "policy", type: "boolean", get label() { return stText("sharePersonalItems"); }, get description() { return stText("allowSharingPersonalMediaItems"); }, defaultValue: false, serverOnly: "emby", intentId: "sharePersonalItems" },
      { key: "AllowCameraUpload", bucket: "policy", type: "boolean", get label() { return stText("cameraUpload"); }, get description() { return stText("allowUploadingPhotosFromDeviceCamera"); }, defaultValue: false, serverOnly: "emby", intentId: "cameraUpload" },
    ],
  },
  {
    id: "livetv",
    get title() { return stText("liveTV"); },
    icon: "lucide:tv",
    accent: "cyan",
    fields: [
      { key: "EnableLiveTvAccess", bucket: "policy", type: "boolean", get label() { return stText("liveTVAccess"); }, get description() { return stText("allowAccessToLiveTVStreams"); }, defaultValue: true, intentId: "liveTvAccess" },
      { key: "EnableLiveTvManagement", bucket: "policy", type: "boolean", get label() { return stText("liveTVManagement"); }, get description() { return stText("allowManagingLiveTVSettings"); }, defaultValue: false, intentId: "liveTvManagement" },
    ],
  },
  {
    id: "features",
    get title() { return stText("featuresControl"); },
    icon: "lucide:settings",
    accent: "indigo",
    fields: [
      { key: "EnableRemoteControlOfOtherUsers", bucket: "policy", type: "boolean", get label() { return stText("remoteControlOthers"); }, get description() { return stText("allowRemoteControllingOtherUsersSessions"); }, defaultValue: false, intentId: "remoteControlOthers" },
      { key: "EnableSharedDeviceControl", bucket: "policy", type: "boolean", get label() { return stText("sharedDeviceControl"); }, get description() { return stText("allowControllingSharedDevices"); }, defaultValue: true, intentId: "sharedDeviceControl" },
      { key: "EnableUserPreferenceAccess", bucket: "policy", type: "boolean", get label() { return stText("userPreferenceAccess"); }, get description() { return stText("allowAccessToUserPreferences"); }, defaultValue: true, intentId: "userPreferenceAccess" },
      { key: "RestrictedFeatures", bucket: "policy", type: "string[]", get label() { return stText("restrictedFeatures"); }, get description() { return stText("listOfRestrictedFeatureNames"); }, defaultValue: [], serverOnly: "emby", intentId: "restrictedFeatures" },
    ],
  },
  {
    id: "preferences",
    get title() { return stText("preferencesConfig"); },
    icon: "lucide:sliders-horizontal",
    accent: "slate",
    fields: [
      { key: "AudioLanguagePreference", bucket: "config", type: "string", get label() { return stText("audioLanguage"); }, get description() { return stText("preferredAudioLanguageEGEngFre"); }, defaultValue: "", intentId: "audioLanguage" },
      { key: "SubtitleLanguagePreference", bucket: "config", type: "string", get label() { return stText("subtitleLanguage"); }, get description() { return stText("preferredSubtitleLanguageEGEngFre"); }, defaultValue: "", intentId: "subtitleLanguage" },
      { key: "SubtitleMode", bucket: "config", type: "enum", get label() { return stText("subtitleMode"); }, get description() { return stText("defaultSubtitleDisplayMode"); }, defaultValue: "Default", enumOptions: subtitleModeOptions, intentId: "subtitleMode" },
      { key: "PlayDefaultAudioTrack", bucket: "config", type: "boolean", get label() { return stText("playDefaultAudio"); }, get description() { return stText("playTheDefaultAudioTrackAutomatically"); }, defaultValue: true, intentId: "playDefaultAudio" },
      { key: "DisplayMissingEpisodes", bucket: "config", type: "boolean", get label() { return stText("showMissingEpisodes"); }, get description() { return stText("showMissingEpisodesInSeriesViews"); }, defaultValue: false, intentId: "showMissingEpisodes" },
      { key: "EnableNextEpisodeAutoPlay", bucket: "config", type: "boolean", get label() { return stText("autoPlayNext"); }, get description() { return stText("automaticallyPlayTheNextEpisode"); }, defaultValue: true, intentId: "autoPlayNext" },
      { key: "HidePlayedInLatest", bucket: "config", type: "boolean", get label() { return stText("hidePlayedInLatest"); }, get description() { return stText("hideAlreadyPlayedItemsFromLatestMedia"); }, defaultValue: false, intentId: "hidePlayedInLatest" },
      { key: "RememberAudioSelections", bucket: "config", type: "boolean", get label() { return stText("rememberAudio"); }, get description() { return stText("rememberAudioTrackSelectionsAcrossPlayback"); }, defaultValue: true, intentId: "rememberAudio" },
      { key: "RememberSubtitleSelections", bucket: "config", type: "boolean", get label() { return stText("rememberSubtitles"); }, get description() { return stText("rememberSubtitleSelectionsAcrossPlayback"); }, defaultValue: true, intentId: "rememberSubtitles" },
      { key: "IntroSkipMode", bucket: "config", type: "enum", get label() { return stText("introSkip"); }, get description() { return stText("behaviorForSkippingIntros"); }, defaultValue: "None", enumOptions: introSkipOptions, serverOnly: "emby", intentId: "introSkipMode" },
    ],
  },
  {
    id: "wsd",
    get title() { return stText("wSDEnforcement"); },
    icon: "lucide:zap",
    accent: "orange",
    fields: [
      { key: "bypassKillStream", bucket: "wsdLocal", type: "boolean", get label() { return stText("bypassKillStream"); }, get description() { return stText("exemptFromPauseWarningsAndAutomaticStops"); }, defaultValue: false, intentId: "bypassKillStream" },
    ],
  },
];
